import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, HelpCircle, CheckCircle, AlertTriangle, Send, X,
  BookOpen, Shield, Zap, Eye, MessageCircle, ChevronDown
} from 'lucide-react';

const defaultScenario = { /* ... same as before ... */ };

const QnASimulator = ({ scenario = defaultScenario, onComplete, challenge }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showBrief, setShowBrief] = useState(false); // controls balloon
  const navigate = useNavigate();

  const activeScenario = scenario && scenario.question ? scenario : defaultScenario;
  const isCorrect = selectedAnswer === activeScenario.answer;

  // Auto-show brief on mount with fade-in
  useEffect(() => {
    setShowBrief(true);
  }, []);

  const handleSelectAnswer = (choiceKey) => {
    if (isSubmitted) return;
    setSelectedAnswer(choiceKey);
  };

  const handleSubmit = () => {
    if (!selectedAnswer) return;
    setIsSubmitted(true);
    setShowFeedbackModal(true);
  };

  // const handleCloseFeedbackModal = () => {
  //   setShowFeedbackModal(false);
  //   if (onComplete && typeof onComplete === 'function') {
  //     onComplete({
  //       scenarioId: activeScenario.id,
  //       selectedAnswer,
  //       isCorrect: selectedAnswer === activeScenario.answer,
  //     });
  //   }
  // };
  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    if (onComplete && typeof onComplete === 'function') {
      const isAnswerCorrect = selectedAnswer === activeScenario.answer;
      onComplete({
        scenarioId: activeScenario.id,
        selectedAnswer,
        isCorrect: isAnswerCorrect,
        total: 1,
        correct: isAnswerCorrect ? 1 : 0,
        incorrect: isAnswerCorrect ? 0 : 1,
        decisions: {
          [activeScenario.id || 'qna-step']: {
            choice: selectedAnswer,
            correct: isAnswerCorrect,
            submittedAt: new Date().toISOString()
          }
        }
      });
    }
  };

  const handleLearnMore = () => {
    const meta = challenge?.metadata;

    // Support older challenges where metadata was a plain URL string
    if (typeof meta === 'string' && meta.startsWith('http')) {
      window.open(meta, '_blank', 'noopener,noreferrer');
      return;
    }

    // Support newer JSON metadata objects with explicit URLs
    if (meta && typeof meta === 'object') {
      const url =
        meta.learnMoreUrl ||
        meta.learn_more_url ||
        meta.docUrl ||
        meta.doc_url ||
        meta.url;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // Fallback to arena-based navigation
    const arenaId = activeScenario?.arenaId || 'phishing_social_engineering';
    if (arenaId) {
      navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };

  const getChoiceBorderColor = (key) => {
    if (!isSubmitted) {
      return selectedAnswer === key
        ? 'border-cyan-400 ring-4 ring-cyan-400/30 shadow-lg shadow-cyan-500/20'
        : 'border-gray-600 hover:border-cyan-500 hover:ring-4 hover:ring-cyan-500/20';
    }
    if (key === activeScenario.answer)
      return 'border-emerald-500 bg-emerald-50/80 ring-4 ring-emerald-400/30 shadow-xl shadow-emerald-500/30';
    if (key === selectedAnswer && !isCorrect)
      return 'border-rose-500 bg-rose-50/80 ring-4 ring-rose-400/30 shadow-xl shadow-rose-500/30';
    return 'border-gray-500/50';
  };

  const renderFeedbackModal = () => {
    if (!showFeedbackModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-4 sm:p-8 m-2 sm:m-4 animate-fade-in-up text-gray-800 max-h-[92dvh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {isCorrect ? (
                <CheckCircle size={28} className="text-green-600" />
              ) : (
                <AlertTriangle size={28} className="text-red-600" />
              )}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </h2>
            </div>
            <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <div className="text-gray-700 space-y-4">
            {activeScenario.feedback && activeScenario.feedback[selectedAnswer] && (
              <p className="text-base font-medium">{activeScenario.feedback[selectedAnswer]}</p>
            )}
            {activeScenario.explanation && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-blue-800">Detailed Explanation:</h3>
                <p className="text-gray-700 mt-1">{activeScenario.explanation}</p>
              </div>
            )}

            {activeScenario.indicators && activeScenario.indicators.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-yellow-800 flex items-center">
                  <AlertTriangle size={16} className="mr-2" />
                  Key Indicators to Notice:
                </h3>
                <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                  {activeScenario.indicators.map((indicator, index) => (
                    <li key={index}>{indicator}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 sm:gap-4">
            <button
              onClick={handleLearnMore}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} />
              Learn More
            </button>
            <button
              onClick={handleCloseFeedbackModal}
              className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[100dvh] p-3 sm:p-6 font-sans bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-x-hidden">
      <div className="w-full max-w-5xl mx-auto text-white">

        {/* === BALLOON BRIEF POPUP === */}
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 pointer-events-none ${showBrief ? 'animate-in fade-in zoom-in duration-500' : 'animate-out fade-out zoom-out duration-300'
            }`}
        >
          <div className={`pointer-events-auto max-w-3xl w-full mx-3 sm:mx-4 ${showBrief ? 'block' : 'hidden'}`}>
            {/* Sun-bathed Orange-to-Black Gradient Balloon */}
            <div className="relative bg-gradient-to-br from-amber-100/95 via-orange-100/90 to-black/90 backdrop-blur-2xl border border-amber-400/50 rounded-3xl p-4 sm:p-7 shadow-2xl shadow-amber-500/30 transform transition-all duration-500">
              {/* Balloon Tail (warm orange) */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-amber-400/60"></div>

              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/30 rounded-lg animate-pulse">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-amber-800 tracking-wider">MISSION BRIEF</h2>
                </div>
                <button
                  onClick={() => setShowBrief(false)}
                  className="text-amber-700 hover:text-amber-900 transition-colors p-1"
                >
                  <ChevronDown size={26} />
                </button>
              </div>

              <div className="bg-gradient-to-r from-amber-200/40 to-transparent p-5 rounded-xl border-l-4 border-amber-500">
                <p className="text-amber-900 leading-relaxed text-sm sm:text-base font-medium tracking-wide">
                  {activeScenario.scenario}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === FLOATING CHAT BUBBLE (Minimized Brief) === */}
        {!showBrief && (
          <button
            onClick={() => setShowBrief(true)}
            className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 p-3 sm:p-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full shadow-2xl hover:from-amber-400 hover:to-orange-500 hover:scale-110 transition-all duration-300 group"
          >
            <MessageCircle size={24} className="text-white group-hover:animate-pulse" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></span>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"></span>
          </button>
        )}

        {/* === MAIN CONTENT: Question + Choices === */}
        <div className={`transition-opacity duration-500 ${showBrief ? 'opacity-30' : 'opacity-100'}`}>

          {/* === QUESTION CARD === */}
          <div className="mb-4 sm:mb-6">
            <div className="bg-gradient-to-br from-indigo-900/80 via-purple-900/80 to-pink-900/80 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-purple-500/30 shadow-xl">
              <div className="flex items-start gap-3">
                <div className="p-2 sm:p-2.5 bg-white/10 rounded-full backdrop-blur-sm">
                  <HelpCircle className="w-5 h-5 sm:w-7 sm:h-7 text-yellow-400" />
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-white leading-snug tracking-wide drop-shadow-md break-words">
                  {activeScenario.question}
                </h3>
              </div>
            </div>
          </div>

          {/* === CHOICES === */}
          <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
            {Object.entries(activeScenario.choices).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleSelectAnswer(key)}
                disabled={isSubmitted}
                className={`
                  w-full text-left p-3 sm:p-5 rounded-2xl border-2 transition-all duration-300 
                  flex items-start gap-3 sm:gap-4 group relative overflow-hidden text-sm sm:text-base
                  ${getChoiceBorderColor(key)}
                  ${!isSubmitted ? 'cursor-pointer' : 'cursor-not-allowed opacity-90'}
                  backdrop-blur-sm bg-white/5
                `}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div
                  className={`
                    flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center font-bold text-sm sm:text-base
                    transition-all duration-300
                    ${selectedAnswer === key ? 'bg-cyan-500 text-white shadow-lg' : 'bg-gray-700 text-gray-300'}
                    ${!isSubmitted && 'group-hover:scale-110 group-hover:shadow-xl'}
                  `}
                >
                  {key}
                </div>
                <span className="flex-1 text-gray-100 font-medium leading-relaxed pr-1 sm:pr-3 break-words">
                  {value}
                </span>
                {isSubmitted && key === activeScenario.answer && (
                  <CheckCircle className="w-6 h-6 text-emerald-400 animate-bounce" />
                )}
                {isSubmitted && key === selectedAnswer && !isCorrect && (
                  <X className="w-6 h-6 text-rose-400 animate-pulse" />
                )}
              </button>
            ))}
          </div>

          {/* === SUBMIT BUTTON === */}
          {!isSubmitted && (
            <div className="flex justify-center">
              <button
                onClick={handleSubmit}
                disabled={!selectedAnswer}
                className={`
                  group relative w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 text-sm sm:text-base font-bold text-white rounded-2xl
                  transition-all duration-300 transform flex items-center gap-2
                  ${!selectedAnswer
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-600 shadow-2xl hover:shadow-cyan-500/50 hover:scale-105 active:scale-95'
                  }
                `}
              >
                <Send size={20} className="group-hover:translate-x-1 transition-transform" />
                Submit Decision
                <div className="absolute inset-0 rounded-2xl bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
              </button>
            </div>
          )}
        </div>
      </div>

      {renderFeedbackModal()}
    </div>
  );
};

export default QnASimulator;